import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Loader from "../../../components/loader/loader";
import SearchButton from "../../../components/inputs/searchButton";
import Pagination from "../../../components/page/companyDirectory/companyPagination";
import BreadCrumpBack from "../../../components/navigator/back";
import {
  internships_absorbed_directory_list,
  internships_completed_directory_list,
} from "../user.api";
import "../../../components/page/companyDirectory/compDirectory.style.css";
import "./style.css";

const PER_PAGE_OPTIONS = [5, 10, 25, 50];

const safeText = (value) => value || "--";

export default function InternshipDirectoryListing(props) {
  const {
    pageTitle,
    title,
    fetchType,
    emptyMessage,
    dateLabel,
  } = props;

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const myRef = useRef(null);

  const fetchDirectory = () => {
    setIsLoading(true);
    const query = { page, per_page: perPage };
    const apiCall =
      fetchType === "absorbed"
        ? internships_absorbed_directory_list(query)
        : internships_completed_directory_list(query);

    apiCall
      .then((res) => {
        const list = res?.data || [];
        setData(list);

        const pagination = res?.pagination || {};
        const derivedTotal =
          pagination.total ||
          (pagination.last_page ? pagination.last_page * Number(perPage) : list.length);
        setTotalCount(derivedTotal);
      })
      .catch(() => {
        setData([]);
        setTotalCount(0);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchDirectory();
  }, [page, perPage, fetchType]);

  const filteredData = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return data;

    return data.filter((item) => {
      const company = item?.internship_request?.company?.company_name || "";
      const discipline = item?.internship_request?.internship_discipline || "";
      const internName = item?.internship?.full_name || "";
      const institution = item?.internship?.institution?.institution_name || "";

      return [company, discipline, internName, institution].some((field) =>
        field.toLowerCase().includes(keyword)
      );
    });
  }, [data, search]);

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    if (myRef.current) {
      myRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <div ref={myRef}>
      <Loader open={isLoading} />
      <div className="header-banner">
        <div className="tit-txt">{title}</div>
      </div>
      <BreadCrumpBack />

      <div className="comp-layout-con internship-directory-page">
        <div className="top-filter-wrap internship-directory-toolbar">
          <div className="filter-bar-actions internship-directory-toolbar-row">
            <div className="comp-search-box">
              <SearchButton
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                clearSearch={() => setSearch("")}
                placeholder="Search by intern, company, discipline"
                showSearchIcon={false}
              />
            </div>
            <div className="internship-directory-per-page">
              <span>Show</span>
              <select
                className="form-control"
                value={perPage}
                onChange={handlePerPageChange}
              >
                {PER_PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="comp-outer-cont">
          <div className="com-dir-cont">
            <div className="internship-directory-summary">
              <h3>{pageTitle}</h3>
            </div>

            <div className="comp-list-con internship-directory-grid">
          {filteredData.length ? (
            filteredData.map((item) => {
              const company = item?.internship_request?.company;
              const intern = item?.internship;
              const primaryDate =
                fetchType === "absorbed"
                  ? item?.updated_at
                  : item?.completion_date;

              return (
                <div key={item.id} className="comp-list-item-cont">
                  <div className="comp-list-item internship-directory-card">
                    <div className="comp-item-comp-title internship-directory-title">
                      {safeText(company?.company_name)}
                    </div>

                    <div className="comp-item-header internship-directory-card-top">
                      <div className="comp-item-logo internship-directory-avatar">
                        {safeText(intern?.full_name).trim().charAt(0).toUpperCase()}
                      </div>

                      <div className="comp-item-header-data internship-directory-head">
                        <div className="comp-item-person-name internship-directory-name">
                          {safeText(intern?.full_name)}
                        </div>

                        <div className="internship-directory-subline">
                          {safeText(item?.internship_request?.internship_discipline)}
                        </div>

                        <div className="internship-directory-subtext">
                          Institution: {safeText(
                            intern?.institution?.institution_name || intern?.institution_name
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="comp-item-dom-cont internship-directory-chip-row">
                      <div className="chip">Start: {item?.start_date ? moment(item.start_date).format("DD MMM YYYY") : "--"}</div>
                      <div className="chip">Completion: {item?.completion_date ? moment(item.completion_date).format("DD MMM YYYY") : "--"}</div>
                      <div className="chip">{dateLabel}: {primaryDate ? moment(primaryDate).format("DD MMM YYYY") : "--"}</div>
                    </div>

                    <div className="comp-item-product-prof-cont internship-directory-details">
                      <b>Internship Details</b>

                      <div className="internship-directory-detail-row">
                        <span className="internship-directory-detail-label">Intern</span>
                        <span className="internship-directory-detail-value">
                          {safeText(intern?.full_name)}
                        </span>
                      </div>

                      <div className="internship-directory-detail-row">
                        <span className="internship-directory-detail-label">Discipline</span>
                        <span className="internship-directory-detail-value">
                          {safeText(item?.internship_request?.internship_discipline)}
                        </span>
                      </div>

                      <div className="internship-directory-detail-row">
                        <span className="internship-directory-detail-label">Institution</span>
                        <span className="internship-directory-detail-value">
                          {safeText(
                            intern?.institution?.institution_name || intern?.institution_name
                          )}
                        </span>
                      </div>

                      <div className="internship-directory-detail-row">
                        <span className="internship-directory-detail-label">Period</span>
                        <span className="internship-directory-detail-value">
                          {item?.start_date ? moment(item.start_date).format("DD MMM YYYY") : "--"}
                          {" to "}
                          {item?.completion_date
                            ? moment(item.completion_date).format("DD MMM YYYY")
                            : "--"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-data-found internship-directory-empty">
              {emptyMessage}
            </div>
          )}
            </div>
          </div>
        </div>

        <Pagination
          className="pagination-bar"
          currentPage={page}
          totalCount={totalCount}
          pageSize={perPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}
